import * as React from "react";

import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, tv } from "tailwind-variants";

const buttonVariants = tv({
  // "ring-offset-background focus-visible:ring-accent/75"
  base: "flex cursor-pointer items-center justify-center rounded-md border-0 text-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 active:scale-95",
  variants: {
    variant: {
      primary:
        "border-0 border-transparent bg-brand-primary px-4 text-base font-normal text-white disabled:border disabled:border-solid disabled:border-zinc-300 disabled:bg-neutral-100 disabled:text-[#00000040]",
      secondary:
        "border border-solid border-brand-primary bg-white text-brand-primary hover:border-brand-primary/80 hover:text-brand-primary/80 active:border-brand-primary/95 active:text-brand-primary/95 disabled:cursor-not-allowed disabled:opacity-40",
      link: "bg-transparent text-aubergine-perl-600 underline disabled:text-raven-400",
      white: "bg-dr-white-100 text-base text-aubergine-perl-600",
      ghost: "bg-transparent",
    },
    color: {
      primary:
        "bg-aubergine-perl-500 text-dr-white-600 disabled:border disabled:border-solid disabled:border-zinc-300 disabled:bg-neutral-100 disabled:text-[#00000040]",
    },
    size: {
      sm: "h-8 px-2 text-sm",
      md: "h-9 px-3 text-base",
      lg: "h-10 px-3 text-base",
    },
    block: {
      true: "w-full",
    },
    rounded: {
      none: "rounded-none",
      sm: "rounded-sm",
      md: "rounded-md",
      lg: "rounded-lg",
      full: "rounded-full",
    },
  },
  defaultVariants: {
    variant: "primary",
    size: "md",
    rounded: "full",
  },
});

type ButtonVariants = VariantProps<typeof buttonVariants>;

interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    ButtonVariants {
  color?: "primary";
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      color,
      size,
      rounded,
      block,
      asChild = false,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={buttonVariants({
          variant,
          color,
          size,
          rounded,
          block,
          className,
        })}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export type { ButtonProps };

export { Button, buttonVariants };
